/**
 * Component แสดงผลรางวัลหวยทุกประเภท — ใช้ร่วมกันระหว่าง LotteryResultPage และ ClassicHomePage
 * ต้นแบบ: backup_old_version/HtmlUser/HuayAsia/index.html (หน้าแรกดูผลหวยได้โดยไม่ต้อง login)
 *
 * การดึงข้อมูล: เรียกผ่าน Public API เท่านั้น (ไม่ต้อง authen)
 * - useLotteryResults → lotteryService.getLotteryRounds() + getTenantHuayTypes()
 * - WebSocket: ฟัง huay_type_updated / lottery_round_updated เพื่อ refetch (เปิด/ปิดประเภทหวย, ประกาศผล)
 */
import React, { useState, useEffect } from 'react'
import { useLotteryResults } from '../../hooks/useLotteryResults'
import {
  LotteryResultSectionGovernment,
  LotteryResultSectionSimple,
  LotteryResultSectionSet,
  LotteryResultSectionGroup,
  LotteryResultSectionYeekee,
  LotteryResultSectionYeekeeVip,
} from './index'
import {
  LotteryResultStaticGovernment,
  LotteryResultStaticYeekeeCurrent,
  LotteryResultStaticEgov,
  LotteryResultStaticGsb,
  LotteryResultStaticForeignStock,
} from './static'
import type { LotteryRound } from '../../services/lotteryService'
import { getApiBaseUrl } from '../../lib/apiBaseUrl'

export interface LotteryResultSectionsProps {
  selectedDate: string
  /** เมื่อมีค่า จะไม่โหลด pool (ใช้ใน LotteryResultPage โหมด roundId) */
  targetRoundId?: string | null
  /** คลิกจับยี่กี → navigate ไป /yeekee-result */
  onYeekeeRoundClick?: (round: LotteryRound) => void
  /** คลิกจับยี่กี VIP → navigate ไป /yeekee-result */
  onYeekeeVipRoundClick?: (round: LotteryRound) => void
  /** แสดง error message เมื่อ API ล้มเหลว */
  showPoolError?: boolean
  /** className สำหรับ wrapper (เช่น row) */
  className?: string
}

const LotteryResultSections: React.FC<LotteryResultSectionsProps> = ({
  selectedDate,
  targetRoundId = null,
  onYeekeeRoundClick,
  onYeekeeVipRoundClick,
  showPoolError = true,
  className = 'row mx--1',
}) => {
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  useEffect(() => {
    const onHuayTypeUpdated = () => setRefreshTrigger((t) => t + 1)
    const onRoundUpdated = () => setRefreshTrigger((t) => t + 1)
    window.addEventListener('huay_type_updated', onHuayTypeUpdated)
    window.addEventListener('lottery_round_updated', onRoundUpdated)
    return () => {
      window.removeEventListener('huay_type_updated', onHuayTypeUpdated)
      window.removeEventListener('lottery_round_updated', onRoundUpdated)
    }
  }, [])

  const {
    poolError,
    latestGovernmentRound,
    latestYeekeeRound,
    latestEgovRound,
    latestGsbRound,
    latestSetRounds,
    latestThaiStockRounds,
    latestForeignRounds,
    latestForeignStockRounds,
    latestVipRounds,
    latestVisaRounds,
    allYeekeeVipRounds,
  } = useLotteryResults(selectedDate, { targetRoundId, refreshTrigger })

  const apiBase = getApiBaseUrl() || window.location.origin
  const friendlyMessage =
    poolError && /502|Bad Gateway/i.test(poolError)
      ? `Backend ไม่ตอบ (502) — ตรวจสอบว่า Lottery Service รันอยู่ และ Load Balancer ชี้ไปที่ถูกต้อง (${apiBase})`
      : poolError && /503/i.test(poolError)
        ? `Service ไม่พร้อม (503) — รอสักครู่หรือตรวจสอบ Backend ที่ ${apiBase}`
        : poolError && /404/i.test(poolError)
          ? `ไม่พบ path API (404) — ตรวจสอบว่า Backend รองรับ Public API ที่ ${apiBase}`
          : poolError
            ? `${poolError} — ด้านล่างเป็นผลแบบสถิต (ไม่ใช่ข้อมูลจริง) จนกว่า API จะทำงาน`
            : null

  return (
    <div className={className}>
      {showPoolError && friendlyMessage && (
        <div className="col-12 px-1 mb-4">
          <div className="wrap-box p-3 bgg-7 bdr-5">
            <p className="c-red mb-1">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {friendlyMessage}
            </p>
            <p className="mb-0 small opacity-75">
              ข้อมูลด้านล่างเป็นแบบสถิต (placeholder) จะแสดงข้อมูลจริงเมื่อเชื่อมต่อ API ได้
            </p>
          </div>
        </div>
      )}
      {!poolError && latestGovernmentRound ? (
        <LotteryResultSectionGovernment sectionTitle="หวยรัฐบาล" rounds={[latestGovernmentRound]} />
      ) : (
        <LotteryResultStaticGovernment />
      )}
      {!poolError && latestYeekeeRound ? (
        <LotteryResultSectionYeekee
          sectionTitle="จับยี่กี"
          rounds={[latestYeekeeRound]}
          onRoundClick={onYeekeeRoundClick}
        />
      ) : (
        <LotteryResultStaticYeekeeCurrent />
      )}
      {!poolError && latestEgovRound ? (
        <LotteryResultSectionSimple sectionTitle="หวยธกส." rounds={[latestEgovRound]} />
      ) : (
        <LotteryResultStaticEgov />
      )}
      {!poolError && latestGsbRound ? (
        <LotteryResultSectionSimple sectionTitle="หวยออมสิน" rounds={[latestGsbRound]} />
      ) : (
        <LotteryResultStaticGsb />
      )}
      {!poolError && latestSetRounds.length > 0 &&
        <LotteryResultSectionSet sectionTitle="หวยชุด" rounds={latestSetRounds} />
      }
      {!poolError && latestThaiStockRounds.length > 0 &&
        <LotteryResultSectionGroup sectionTitle="หวยหุ้นไทย" rounds={latestThaiStockRounds} />
      }
      {(() => {
        const showStatic = !poolError && latestForeignStockRounds.length === 0
        if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
          console.log('[LotteryResultSections] หวยหุ้น:', {
            poolError: !!poolError,
            latestForeignStockRoundsLength: latestForeignStockRounds.length,
            showStatic,
            reason: showStatic ? 'ไม่มีข้อมูลรอบหวยหุ้น → แสดง static' : 'มีข้อมูล → แสดง LotteryResultSectionGroup',
          })
        }
        return !poolError && latestForeignStockRounds.length > 0 ? (
          <LotteryResultSectionGroup sectionTitle="หวยหุ้น" rounds={latestForeignStockRounds} />
        ) : (
          !poolError && <LotteryResultStaticForeignStock />
        )
      })()}
      {!poolError && latestForeignRounds.length > 0 &&
        <LotteryResultSectionGroup sectionTitle="หวยต่างประเทศ" rounds={latestForeignRounds} />
      }
      {!poolError && latestVipRounds.length > 0 && (
        <LotteryResultSectionGroup sectionTitle="หวย VIP" rounds={latestVipRounds} />
      )}
      {!poolError && latestVisaRounds.length > 0 && (
        <LotteryResultSectionGroup sectionTitle="หวย VISA" rounds={latestVisaRounds} />
      )}
      {!poolError && allYeekeeVipRounds.length > 0 && (
        <LotteryResultSectionYeekeeVip
          sectionTitle="จับยี่กี VIP"
          rounds={allYeekeeVipRounds}
          onRoundClick={onYeekeeVipRoundClick}
        />
      )}
    </div>
  )
}

export default LotteryResultSections
